import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { $getSelection, $isRangeSelection } from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { mergeRegister } from '@lexical/utils';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { richTextEditorStrings } from '../../../i18n/components/richtTextEditor';

export function LinkPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isLink, setIsLink] = useState(false);
  const [showLinkEditor, setShowLinkEditor] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');
  const [currentLinkUrl, setCurrentLinkUrl] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateLinkState = useCallback(() => {
    const selection = $getSelection();

    if (!$isRangeSelection(selection)) {
      return;
    }

    const node = selection.anchor.getNode();
    const parent = node.getParent();

    let linkNode = null;
    if ($isLinkNode(parent)) {
      linkNode = parent;
    } else if ($isLinkNode(node)) {
      linkNode = node;
    }

    if (!linkNode) {
      setIsLink(false);
      setCurrentLinkUrl(null);
      return;
    }

    setIsLink(true);
    setCurrentLinkUrl(linkNode.getURL());
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkState();
        });
      }),
    );
  }, [editor, updateLinkState]);

  useEffect(() => {
    if (showLinkEditor && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showLinkEditor]);

  const handleOpenLinkEditor = useCallback(() => {
    const url = isLink && currentLinkUrl ? currentLinkUrl : 'https://';
    setLinkUrl(url);
    setShowLinkEditor(true);
  }, [isLink, currentLinkUrl]);

  const handleRemoveLink = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    setShowLinkEditor(false);
    setLinkUrl('https://');
  }, [editor]);

  const handleSaveLink = useCallback(() => {
    if (linkUrl.trim()) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
      setShowLinkEditor(false);
    }
  }, [editor, linkUrl]);

  const handleCancel = useCallback(() => {
    setShowLinkEditor(false);
    setLinkUrl('https://');
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSaveLink();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancel();
      }
    },
    [handleSaveLink, handleCancel],
  );

  return (
    <span className="lexical-toolbar-group" style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpenLinkEditor}
        className={`lexical-toolbar-button ${isLink ? 'active' : ''}`}
        aria-label={richTextEditorStrings.link.button.label}
        title={richTextEditorStrings.link.button.title}
      >
        <i className="pi pi-link" />
      </button>

      {showLinkEditor && (
        <div
          className="lexical-link-editor-popover"
          role="dialog"
          aria-label={richTextEditorStrings.link.dialog.title}
        >
          <div className="lexical-link-editor-content">
            <label
              htmlFor="link-url-input"
              className="lexical-link-editor-label"
            >
              {richTextEditorStrings.link.dialog.label}
            </label>
            <InputText
              id="link-url-input"
              ref={inputRef}
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={richTextEditorStrings.link.dialog.placeholder}
              aria-required="true"
              style={{ width: '100%' }}
            />
            <div className="lexical-link-editor-actions">
              <Button
                type="button"
                onClick={handleSaveLink}
                disabled={!linkUrl.trim()}
                aria-label={richTextEditorStrings.link.actions.save.label}
                icon="pi pi-check"
                label={richTextEditorStrings.link.actions.save.text}
                size="small"
              />
              {isLink && (
                <Button
                  type="button"
                  onClick={handleRemoveLink}
                  aria-label={richTextEditorStrings.link.actions.remove.label}
                  icon="pi pi-trash"
                  label={richTextEditorStrings.link.actions.remove.text}
                  severity="danger"
                  size="small"
                />
              )}
              <Button
                type="button"
                onClick={handleCancel}
                aria-label={richTextEditorStrings.link.actions.cancel.label}
                icon="pi pi-times"
                label={richTextEditorStrings.link.actions.cancel.text}
                outlined
                size="small"
              />
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
